import { useState } from 'react'
import {
  type Employee,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
} from '@/entities/employee'
import { errorMessage } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { toast } from '@/shared/ui/toast'

interface EmployeeDialogProps {
  open: boolean
  /** Задан — правим человека, иначе заводим нового. */
  employee: Employee | null
  onClose: () => void
}

const EMPTY = { name: '', login: '', password: '', role: '', admin: false }

/**
 * Заведение и правка сотрудника. Самозаписи нет: пароль придумывает заводящий и
 * передаёт лично. Должность — подпись под именем в шапке колонки; права — вещь
 * отдельная, и одно из другого не следует.
 *
 * Логин и пароль правятся не здесь: логин неизменяем, пароль выдаётся отдельным
 * действием — иначе форма правки молча перетирала бы доступ человека.
 */
export function EmployeeDialog({ open, employee, onClose }: EmployeeDialogProps) {
  const create = useCreateEmployeeMutation()
  const update = useUpdateEmployeeMutation()
  const [form, setForm] = useState(EMPTY)
  const [key, setKey] = useState<string | null>(null)

  // Диалог переиспользуется между людьми: при смене цели поля перезаполняются,
  // а не тянут прежний ввод.
  const nextKey = employee?.id ?? 'new'
  if (open && key !== nextKey) {
    setKey(nextKey)
    setForm(
      employee
        ? { ...EMPTY, name: employee.name, role: employee.role, admin: employee.isAdmin }
        : EMPTY,
    )
  }
  if (!open && key !== null) setKey(null)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (employee) {
      update.mutate(
        {
          id: employee.id,
          name: form.name.trim(),
          role: form.role.trim(),
          accessRole: form.admin ? 'admin' : 'employee',
        },
        {
          onSuccess: () => {
            onClose()
            toast.success('Сохранено')
          },
        },
      )
      return
    }
    create.mutate(
      {
        name: form.name.trim(),
        login: form.login.trim().toLowerCase(),
        password: form.password,
        ...(form.role.trim() ? { role: form.role.trim() } : {}),
        ...(form.admin ? { accessRole: 'admin' as const } : {}),
      },
      {
        onSuccess: () => {
          onClose()
          toast.success('Сотрудник заведён — передайте пароль лично')
        },
      },
    )
  }

  const pending = create.isPending || update.isPending
  const error = create.error ?? update.error

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={employee ? employee.name : 'Новый сотрудник'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            type="submit"
            form="employee-form"
            disabled={pending || form.name.trim().length === 0}
          >
            {employee ? 'Сохранить' : 'Завести'}
          </Button>
        </>
      }
    >
      <form id="employee-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Имя">
          {(props) => (
            <Input
              {...props}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          )}
        </Field>
        <Field label="Должность" hint="Подпись под именем: гид, водитель, фотограф">
          {(props) => (
            <Input
              {...props}
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            />
          )}
        </Field>

        <Checkbox
          label="Администратор"
          checked={form.admin}
          onChange={(e) => setForm({ ...form, admin: e.target.checked })}
        />
        <p className="-mt-2 text-xs text-text-tertiary">
          Ведёт команду, экскурсии и записи клиентов. Без этого человек видит только своё
          расписание.
        </p>

        {employee ? (
          <p className="text-xs text-text-tertiary">
            Логин <span className="mono-num">{employee.login}</span> не меняется. Пароль выдаётся
            отдельным действием.
          </p>
        ) : (
          <>
            <Field label="Логин" hint="Без пробелов, строчными">
              {(props) => (
                <Input
                  {...props}
                  value={form.login}
                  autoComplete="off"
                  onChange={(e) => setForm({ ...form, login: e.target.value })}
                />
              )}
            </Field>
            <Field label="Пароль" hint="Длинный и не похожий на логин">
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  value={form.password}
                  autoComplete="new-password"
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              )}
            </Field>
          </>
        )}

        {error && (
          <p role="alert" className="text-sm text-accent">
            {errorMessage(error)}
          </p>
        )}
      </form>
    </Dialog>
  )
}
